import './DiagramSelection.css';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { ShoppingCart, Check, Maximize2, AlertTriangle, Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useOrder } from '../../contexts/OrderContext';
import { cartOrderService } from '../../services/CartOrderService';
import { TranslationBatchManager } from '../../services/TranslationService';
import Toast from './Toast';
import { Callout } from '../../models/callout';
import { useToast } from '../../hooks/useToast';
import { PartResult } from '../features/chat/types';
import { drawDiagramTag, DIAGRAM_PALETTE, CalloutPriority, isEstimatedScore, matchBand, MATCH_BAND_LABEL } from '../../utils/diagramTag';
import { catalogPartIdentity } from '../../utils/catalogKits';
import { positionChipsFromExtra, positionClassesFromName, resolvedPositionChips, positionChipSlug, displayPartRef } from '../../utils/partLabel';
import { parseCatalogQuantity } from '../../utils/quantity';
import { collapseSupersededPartRows } from '../../utils/partSupersession';
import SupersessionIndicator from './SupersessionIndicator';
import DiagramModal, { DiagramRow } from './DiagramModal';
import { requestedTypeOf } from '../../utils/partTags';
import { aiRowTagsOf } from '../features/chat/ClarificationHintPanel';
import chatService from '../../services/ChatService';
import { useOrgFeatureFlag } from '../../hooks/useOrgFeatureFlag';
import { useActiveSessionIdSafe } from '../../contexts/SessionContext';

// Normalized spellings of a part's identity (number or id), for joining a
// result card to its diagram row.
const partKeys = (p: any): string[] =>
  Array.from(new Set(['partId', 'partNumber', 'part_number', 'formattedPartNumber']
    .map(k => String(p?.[k] ?? '').toUpperCase().replace(/[^A-Z0-9]/g, ''))
    .filter(Boolean)));

// ── The search result's parts diagram ───────────────────────────────────────
// The card shows the drawing letterboxed with one action over it; the map
// itself is the SHARED viewer (DiagramModal — the same component the
// clarification question opens), so the tags, legend, parts list and ordering
// are identical on both surfaces. What is particular to a result lives here:
// the search-request header strip, add-to-cart, and the "manual selection" /
// in-cart status the question has no notion of.

interface ImageWithCalloutsProps {
  imageData: string;
  calloutData: Callout[];
  partInfo?: PartResult;
  messageId: string;
  partCalloutId?: string | null;
  partId?: string | null; // ID of the part to prioritize/highlight (matches with callout label or part info)
  image_parts?: Array<any> | null;
  youMayLikeParts?: Array<any> | null;
  vin_data?: {
    make: string;
    manufacturer: string;
    model: string;
    trim: string;
    vin: string;
    year: string;
  };
  onOrderUpdate?: () => void;
  orderUpdateTrigger?: number; // Trigger to force refresh when order is updated externally
  onSearchPart?: (searchText: string) => void;
  preTranslatedDescriptions?: Record<string, string> | null; // Pre-translated descriptions map (original text -> translated text)
  onAddToCart?: (part: any, structuredData: any) => Promise<void> | void; // Guest mode: intercept add-to-cart
  /** The resolved display label (corrected name + resolution keys) to use as search term in cart */
  resolvedLabel?: string | null;
  /** Calibrated card score, shared with the header. */
  displayProbability?: number | null;
  /** When true, open the fullscreen map immediately on mount. Lets a thumbnail
   *  elsewhere (e.g. the results table) launch the zoom view without the user
   *  first seeing the inline image. */
  autoOpenModal?: boolean;
  /** Called whenever the fullscreen map closes. A parent that mounted this only
   *  to show the map can use it to unmount the component. */
  onModalClose?: () => void;
}

// Does an OE note's production-date condition ("before 2021-09-14" / "after
// 2021-09-14") FIT the vehicle's model year? true = fits, false = contradicts,
// null = no parseable condition. Mirrors the backend `_year_condition_verdict`:
// year-granular, and the cutoff year itself stays undecidable (a 2021 model may
// be built either side of a 2021 cutoff), so it never over-filters.
const noteDateVerdict = (text: string, year: number): boolean | null => {
  const re = /\b(before|after|until|from)\s*[:-]?\s*((?:19|20)\d{2})(?:[-./]\d{1,2}[-./]\d{1,2})?/gi;
  const verdicts: (boolean | null)[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const word = m[1].toLowerCase();
    const cutoff = parseInt(m[2], 10);
    if (year === cutoff) verdicts.push(null);
    else if (word === 'before' || word === 'until') verdicts.push(year < cutoff);
    else verdicts.push(year > cutoff);
  }
  const known = verdicts.filter((v): v is boolean => v !== null);
  return known.length ? known.some(Boolean) : null;
};

const NOTE_KEYS = ['remark', 'remarks', 'note', 'notes', 'optionalpack', 'optionalpackage'];
const NULLISH = ['n/a', 'na', 'none', 'null', '-', '—'];

const ImageWithCallouts: React.FC<ImageWithCalloutsProps> = ({ imageData, calloutData, partInfo, partCalloutId, partId, image_parts: rawImageParts, vin_data, onOrderUpdate, orderUpdateTrigger, onSearchPart, youMayLikeParts, preTranslatedDescriptions, onAddToCart, resolvedLabel, displayProbability, autoOpenModal, onModalClose }) => {
  const model3d = useOrgFeatureFlag('diagram_3d_models');

  const { t, i18n } = useTranslation();
  const { getActiveOrderId, refreshOrders, getOrderObject, setOrderObject } = useOrder();
  const { toast, success: showSuccessToast, hideToast } = useToast();
  // Catalog payloads include obsolete and current rows together. Keep only the
  // terminal row of each explicit replacement chain on every diagram surface.
  const image_parts = useMemo(
    () => Array.isArray(rawImageParts) ? collapseSupersededPartRows(rawImageParts) : rawImageParts,
    [rawImageParts],
  );
  const [callouts, setCallouts] = useState<Callout[]>([]);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedCallout, setSelectedCallout] = useState<string | null>(null);
  const [selectedPartInfo, setSelectedPartInfo] = useState<any>(null);
  const [isAddingToOrder, setIsAddingToOrder] = useState(false);
  const [orderItemsRefreshTrigger, setOrderItemsRefreshTrigger] = useState(0);
  const [translatedDescription, setTranslatedDescription] = useState<string | null>(null);
  const [translatedCategory, setTranslatedCategory] = useState<string | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  // Cache to avoid re-translating the same content
  const translationCacheRef = useRef<Map<string, { description: string; category: string }>>(new Map());

  // Memoize activeOrderItems to prevent recalculation on every render
  const activeOrderItems = useMemo(() =>
    getOrderObject()?.parts_in_order?.items || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getOrderObject, orderItemsRefreshTrigger, orderUpdateTrigger]
  );

  // The estimate the answered clarification showed for this part, keyed by
  // partId on the card (`clarification_scores`). It outranks the complementary-
  // parts probability labels so the results list keeps the question's numbers
  // and ordering. Without a question, the search's own scored rivals
  // (`alternatives`) play the same role, so a directly resolved part lists its
  // candidates the same way.
  const clarificationScoreOf = useCallback((part: any): number | undefined => {
    const m: Record<string, number> = { ...((partInfo as any)?.clarification_scores || {}) };
    for (const a of ((partInfo as any)?.alternatives || []) as Array<{ partId?: string; probability_score?: number | null }>) {
      if (a?.partId && typeof a.probability_score === 'number' && !(a.partId in m)) m[String(a.partId)] = a.probability_score;
    }
    if (!part) return undefined;
    for (const k of [part.partId, part.partNumber, part.crossCatKey]) {
      const v = k != null ? m[String(k)] : undefined;
      if (typeof v === 'number' && v > 0) return v;
    }
    return undefined;
  }, [partInfo]);

  // The diagram endpoint can return parts that were never in the search's
  // candidate set. Give those rows the same low-band name estimate used for
  // unranked clarification rows; a real search/question score always wins.
  const unrankedScoreOf = useCallback((part: any): number | undefined => {
    if (typeof (partInfo as any)?.probability_score !== 'number') return undefined;
    const anchor = String((partInfo as any)?.part_name || (partInfo as any)?.description || (partInfo as any)?.related_corrected_name || '');
    const name = String(part?.partName || part?.description || part?.name || '');
    const words = (text: string) => new Set(text.toLowerCase().match(/[a-zÀ-ɏ]{4,}/g) || []);
    const requested = words(anchor);
    const matched = Array.from(words(name)).filter(word => requested.has(word)).length;
    return matched ? Math.min(0.45, 0.2 + 0.2 * matched / Math.max(1, requested.size)) : 0.1;
  }, [partInfo]);

  const isRequestedRow = useCallback((part: any, calloutLabel: string): boolean => {
    const selectedIds = [(partInfo as any)?.partId, (partInfo as any)?.partNumber, partId]
      .filter(Boolean).map(String);
    if (selectedIds.length) {
      return [part?.partId, part?.partNumber, part?.part_number]
        .filter(Boolean).some(id => selectedIds.includes(String(id)));
    }
    return !!partCalloutId && partCalloutId.split(',').map(x => x.trim()).includes(calloutLabel);
  }, [partInfo, partId, partCalloutId]);

  // Handles zero-padded IDs (001 vs 1) and position parentheticals (18(RH) vs 18).
  // Alphabetic suffixes identify different parts (62010A != 62010B).
  const matchCallout = (partVal: any, targetLabel: string) => {
    if (!partVal || !targetLabel) return false;
    const p = String(partVal).trim();
    const target = String(targetLabel).trim();
    if (p.toLowerCase() === target.toLowerCase()) return true;
    const pClean = p.replace(/\(.*?\)/g, '').replace(/[^0-9a-zA-Z]/g, '').replace(/^0+/, '');
    const tClean = target.replace(/\(.*?\)/g, '').replace(/[^0-9a-zA-Z]/g, '').replace(/^0+/, '');
    return !!pClean && !!tClean && pClean.toLowerCase() === tClean.toLowerCase();
  };
  const labelMatchesOf = useCallback((calloutLabel: string): any[] => (
    (image_parts && Array.isArray(image_parts) ? image_parts : []).filter(part => (
      matchCallout(part.crossCatKey, calloutLabel) ||
      matchCallout(part.index, calloutLabel) ||
      matchCallout(part.calloutLabel, calloutLabel) ||
      matchCallout(part.callout, calloutLabel) ||
      matchCallout(part.part_callout_id, calloutLabel) ||
      matchCallout(part.partNumber, calloutLabel)
    ))
  ), [image_parts]);

  // The part info for one callout. partId (the hero part's number) is a
  // DISAMBIGUATOR, not a filter: when several rows share the callout label,
  // prefer the hero's row.
  const findPartInfo = useCallback((calloutLabel: string) => {
    const heroFallback = () => (
      partCalloutId && partCalloutId.split(',').map(s => s.trim()).includes(calloutLabel) && partInfo
        ? { ...partInfo }
        : null
    );
    if (!image_parts || !Array.isArray(image_parts)) return heroFallback();
    const labelMatches = labelMatchesOf(calloutLabel);
    const part =
      labelMatches.find(p => partId && partId !== '' && (partId === String(p.partId) || partId === String(p.partNumber))) ??
      labelMatches[0];
    if (!part) return heroFallback();
    // The RECOMMENDED part keeps the score the search gave it; anything else
    // genuinely was never scored against the request.
    const isRequested = isRequestedRow(part, calloutLabel);
    const score = clarificationScoreOf(part) ?? (isRequested ? (partInfo as any)?.probability_score : undefined) ?? unrankedScoreOf(part);
    return { ...partInfo, ...part, description: part.description || part.name || part.partName || '', extra_details: part.extra_details || {}, choice_guidance: part.choice_guidance ?? null, probability_score: score };
  }, [image_parts, partInfo, partCalloutId, partId, labelMatchesOf, clarificationScoreOf, unrankedScoreOf, isRequestedRow]);

  // ALL parts that match a callout label (not just the hero pick). Several
  // distinct parts can share a callout (left vs right at the same location —
  // they differ by side + partId); each gets its own list row.
  const findAllPartInfos = useCallback((calloutLabel: string): any[] => {
    const labelMatches = labelMatchesOf(calloutLabel);
    if (labelMatches.length === 0) {
      const one = findPartInfo(calloutLabel);
      return one ? [one] : [];
    }
    const seen = new Set<string>();
    const out: any[] = [];
    for (const part of labelMatches) {
      // De-dupe by part identity — the OE/part NUMBER is the divider.
      const idKey = String(part.partNumber ?? part.partId ?? part.crossCatKey ?? '');
      if (idKey && seen.has(idKey)) continue;
      if (idKey) seen.add(idKey);
      const isRequested = isRequestedRow(part, calloutLabel);
      out.push({ ...partInfo, ...part, description: part.description || part.name || part.partName || '', extra_details: part.extra_details || {}, choice_guidance: part.choice_guidance ?? null, probability_score: clarificationScoreOf(part) ?? (isRequested ? (partInfo as any)?.probability_score : undefined) ?? unrankedScoreOf(part) });
    }
    return out;
  }, [partInfo, labelMatchesOf, findPartInfo, clarificationScoreOf, unrankedScoreOf, isRequestedRow]);

  // Side/position + OE-note values for a part (the note values also drive the
  // production-date fit check).
  const chipDataFor = useCallback((info: any): { positions: string[]; notes: string[] } => {
    const ed = info?.extra_details;
    const positions = positionChipsFromExtra(ed);
    const notes: string[] = [];
    if (ed && typeof ed === 'object' && !Array.isArray(ed)) {
      const seen = new Set<string>();
      for (const [k, v] of Object.entries(ed as Record<string, unknown>)) {
        if (!NOTE_KEYS.includes(String(k).toLowerCase().replace(/[\s_-]/g, ''))) continue;
        const val = String(v ?? '').trim();
        const norm = val.toLowerCase();
        if (!val || NULLISH.includes(norm) || seen.has(norm)) continue;
        seen.add(norm);
        notes.push(val);
      }
    }
    return { positions, notes };
  }, []);

  const tPos = useCallback((word: string): string => {
    const slug = positionChipSlug(word);
    return slug ? String(t(`chat.position.${slug}`, { defaultValue: word })) : word;
  }, [t]);

  const isPartInOrder = useCallback((info: any, calloutLabel: string): boolean => {
    if (!info) return false;
    const pid = String(info.partNumber ?? info.crossCatKey ?? calloutLabel);
    return activeOrderItems.some((item: any) => item.structured_data?.part_data?.part_id === pid);
  }, [activeOrderItems]);

  const isYouMayLikePart = useCallback((calloutLabel: string): boolean => {
    if (!youMayLikeParts || !Array.isArray(youMayLikeParts) || youMayLikeParts.length === 0) return false;
    const partData = findPartInfo(calloutLabel);
    if (!partData) return false;
    const partDescription = (partData.description || partData.name || '').toLowerCase();
    return youMayLikeParts.some(ymlPart => {
      const ymlName = (ymlPart.suggested_part_name || '').toLowerCase();
      return ymlName && partDescription && (ymlName === partDescription || partDescription.includes(ymlName) || ymlName.includes(partDescription));
    });
  }, [youMayLikeParts, findPartInfo]);

  // Parse partCalloutId as a list (supports comma-separated multiple IDs)
  const partCalloutIds = useMemo((): string[] => {
    if (!partCalloutId) return [];
    return partCalloutId.split(',').map(s => s.trim()).filter(Boolean);
  }, [partCalloutId]);
  const isRecommendedPart = useCallback((calloutLabel: string): boolean => partCalloutIds.includes(calloutLabel), [partCalloutIds]);

  // The card's part was picked BY THE USER when answering a clarification
  // (`user_selected`, stamped server-side from what they actually clicked). The
  // diagram must say so: a navy tag alone reads as "the resolver chose this".
  // Scoped to the hero callout(s) — the flag is a fact about THIS card's part.
  // "Manual selection" means clicked on the drawing itself. A part reached by
  // answering one of the question's listed options is a confirmed answer,
  // not a manual pick — the chip would only repeat what the request line says.
  const userPicked = !!(partInfo as any)?.user_picked_on_diagram;
  const isUserPickedInfo = useCallback((calloutLabel: string, info: any): boolean => {
    if (!userPicked || !partCalloutIds.includes(calloutLabel)) return false;
    // A hero callout can carry several parts (left/right at the same ref); only
    // the one whose identity matches the card's part is the pick.
    if (!partId || !info) return true;
    const id = info.partNumber ?? info.partId ?? info.crossCatKey;
    return id == null || String(id) === String(partId);
  }, [userPicked, partCalloutIds, partId]);

  // Priority ramp per the Catalog Parts Illustration Guidelines (§3): search
  // relevance → one of four navy-ramp levels. Status (selected/in-cart) is NOT
  // a priority — it overlays via halo/badge.
  const calloutPriority = useCallback((calloutLabel: string, info: any): CalloutPriority => {
    if (isRecommendedPart(calloutLabel)) return 'hero';
    if (!info) return 'na'; // no catalog info → not available
    // A candidate the clarification question offered keeps its place in the list.
    if (clarificationScoreOf(info) !== undefined) return 'secondary';
    return 'optional';
  }, [isRecommendedPart, clarificationScoreOf]);

  // Helper function to get absolute bounds from callout (handles both normalized and absolute coordinates)
  const getAbsoluteBounds = useCallback((callout: Callout, imageWidth: number, imageHeight: number) => {
    if (!callout?.bounds) return { x: 0, y: 0, w: 0, h: 0 };
    if (callout.normalized) {
      return {
        x: callout.bounds.x * imageWidth,
        y: callout.bounds.y * imageHeight,
        w: callout.bounds.w * imageWidth,
        h: callout.bounds.h * imageHeight,
      };
    }
    return callout.bounds;
  }, []);

  // Bake the chosen callout onto a copy of the drawing — the cart thumbnail, so
  // the line in the order shows WHICH part on the diagram it is.
  const createHighlightedImage = useCallback((calloutLabel: string, info: any): Promise<string> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve(imageData); return; }

      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const targetCallout = callouts.find(callout => callout.label === calloutLabel);
        if (targetCallout) {
          const b = getAbsoluteBounds(targetCallout, canvas.width, canvas.height);
          // "SELECTED" is a transient viewer state, not a property of the part,
          // so it is never baked into the cart image.
          drawDiagramTag(ctx, b.x + b.w / 2, b.y + b.h / 2, {
            number: targetCallout.label,
            priority: calloutPriority(calloutLabel, info),
            estimated: isEstimatedScore(info?.probability_score),
            score: info?.probability_score,
            inCart: true,
            scale: 1,
          });
        }
        // A CORS-tainted canvas throws on toDataURL — fall back to the original
        // image URL so add-to-cart still works (just without the baked-in tag).
        try { resolve(canvas.toDataURL()); } catch { resolve(imageData); }
      };
      img.onerror = () => { resolve(imageData); };
      // Cache-busted CORS fetch: avoids a plain-cached, non-CORS entry that
      // would taint the canvas and break toDataURL.
      img.src = /^https?:/i.test(imageData)
        ? `${imageData}${imageData.includes('?') ? '&' : '?'}x-cors=1`
        : imageData;
    });
  }, [imageData, callouts, getAbsoluteBounds, calloutPriority]);

  // Add a specific part (by callout label + its resolved info) to cart or order.
  const addInfoToCart = useCallback(async (label: string, info: any) => {
    if (!label || !info) return;
    setIsAddingToOrder(true);
    try {
      const highlightedImageData = await createHighlightedImage(label, info);
      const rowTranslated =
        preTranslatedDescriptions?.[(info.description || info.name || '').trim()] ||
        (label === selectedCallout ? translatedDescription : null) ||
        null;

      const safeVinData = vin_data ?? { make: '', manufacturer: '', model: '', trim: '', vin: '', year: '' };
      const structuredData = {
        vin_data: safeVinData,
        part_data: {
          part_id: String(info.partNumber ?? info.crossCatKey ?? label),
          part_number: String(info.partNumber ?? 'N/A'),
          description: String(info.description ?? info.name ?? `Part ${label}`),
          price: info.pricing_data ?? String(info.price_data) ?? '--',
          // Cart "search" title = the user's ORIGINAL term, not the full label.
          query: String((partInfo as any)?.related_user_query ?? info.related_user_query ?? ''),
        },
        selection_reason: String(info.description ?? 'Selected from image callout'),
      };
      const cartItem = {
        part_id: String(info.partNumber ?? info.crossCatKey ?? label),
        part_callout_id: label,
        structured_data: structuredData,
        image: highlightedImageData,
        imageCallout: calloutData,
        translated_description: rowTranslated,
      };

      // ── Guest mode: delegate to the parent handler with a raw part object
      // (same shape as MultiPartResultView's "Add All" path).
      if (onAddToCart) {
        const rawPart = {
          partNumber: String(info.partNumber ?? label),
          partId: String(info.partNumber ?? info.crossCatKey ?? label),
          description: String(info.description ?? info.name ?? `Part ${label}`),
          price: info.price ?? info.cost ?? null,
          image: highlightedImageData,
          related_user_query: resolvedLabel ?? info.related_corrected_name ?? null,
          translated_description: rowTranslated,
          structured_data: info.structured_data || null,
          quantity: (info as any).quantity,
        };
        // Await the parent handler so the spinner stays visible until the cart
        // update completes and the item appears in the cart.
        await new Promise<void>(resolve => setTimeout(resolve, 0));
        await onAddToCart(rawPart, structuredData);
        return;
      }

      // Pass current order items from memory to skip the GET round-trip inside addItemToOrder.
      const currentOrderItems = getOrderObject()?.parts_in_order?.items ?? [];
      const result = await cartOrderService.addItem(cartItem, getActiveOrderId()!, [], currentOrderItems, parseCatalogQuantity((info as any).quantity));
      if (result.addedToOrder) {
        if (result.updatedOrder) setOrderObject(result.updatedOrder);
        setOrderItemsRefreshTrigger(prev => prev + 1);
        refreshOrders();
        if (onOrderUpdate) onOrderUpdate();
        showSuccessToast(t('imageWithCallouts.notifications.addedToOrder'), structuredData.part_data.description);
      } else {
        console.warn('No active order to add part to');
        showSuccessToast(t('imageWithCallouts.notifications.error') || 'Error', 'Please create an order first');
      }
    } catch (error) {
      console.error('Failed to add item:', error);
      showSuccessToast(
        t('imageWithCallouts.notifications.error') || 'Error',
        t('imageWithCallouts.notifications.failedToAdd') || 'Failed to add item',
      );
    } finally {
      setIsAddingToOrder(false);
    }
  }, [selectedCallout, createHighlightedImage, vin_data, partInfo, resolvedLabel, calloutData, preTranslatedDescriptions, getActiveOrderId, getOrderObject, setOrderObject, refreshOrders, onOrderUpdate, onAddToCart, showSuccessToast, t, translatedDescription]);

  const addToCart = useCallback(
    () => addInfoToCart(selectedCallout as string, selectedPartInfo),
    [addInfoToCart, selectedCallout, selectedPartInfo],
  );

  // "You may also like" suggestion that is NOT a catalog row on this diagram:
  // find it in image_parts by suggested_part_id, else fall back to a search.
  const addYmlPartToCart = useCallback(async (calloutLabel: string) => {
    if (!youMayLikeParts || !Array.isArray(youMayLikeParts)) return;
    const partData = findPartInfo(calloutLabel);
    const partDescription = partData ? (partData.description || partData.name || '') : '';
    const ymlPart = youMayLikeParts.find(yp => {
      const ymlName = (yp.suggested_part_name || '').toLowerCase();
      const descLower = partDescription.toLowerCase();
      return ymlName && (ymlName === descLower || descLower.includes(ymlName) || ymlName.includes(descLower));
    });
    if (!ymlPart) {
      if (onSearchPart) onSearchPart(calloutLabel);
      return;
    }
    const suggestedPartId = ymlPart.suggested_part_id;
    // The hero `partId` is NOT required to match here — a complementary
    // suggestion is by definition a different part than the hero.
    const matchedPart = (suggestedPartId && image_parts && Array.isArray(image_parts))
      ? image_parts.find(p => (
        String(p.crossCatKey) === String(suggestedPartId) ||
        String(p.partNumber) === String(suggestedPartId) ||
        String(p.part_id) === String(suggestedPartId) ||
        String(p.index) === String(suggestedPartId)
      ))
      : null;
    if (!matchedPart) {
      if (onSearchPart) onSearchPart(ymlPart.suggested_part_name || calloutLabel);
      return;
    }
    setSelectedCallout(calloutLabel);
    setSelectedPartInfo({ ...partInfo, ...matchedPart });
    await addInfoToCart(calloutLabel, { ...partInfo, ...matchedPart, description: matchedPart.description || matchedPart.name || matchedPart.partName || '' });
  }, [youMayLikeParts, image_parts, findPartInfo, partInfo, addInfoToCart, onSearchPart]);

  useEffect(() => { if (calloutData) setCallouts(calloutData); }, [calloutData]);
  useEffect(() => { setImageLoaded(false); }, [imageData]);

  // Auto-select the recommended part so the details panel below the image is
  // never empty. Only for the non-fullscreen view.
  useEffect(() => {
    if (partCalloutIds.length > 0 && !showModal) {
      const matchedCallout = callouts.find(c => partCalloutIds.includes(c.label));
      const matchedLabel = matchedCallout?.label ?? partCalloutIds[0];
      setSelectedCallout(matchedLabel);
      setSelectedPartInfo(findPartInfo(matchedLabel));
    }
  }, [partCalloutIds, callouts, showModal, findPartInfo, image_parts]);

  // A caller opening the diagram for a concrete catalog part (for example from
  // "You might also need") passes that part's ID/number through `partId`.
  // Select its own callout instead of the original hero part.
  useEffect(() => {
    if (!autoOpenModal || !partId || !Array.isArray(image_parts)) return;
    const target = image_parts.find((p: any) =>
      String(p.partId) === String(partId) ||
      String(p.partNumber) === String(partId) ||
      String(p.part_number) === String(partId) ||
      String(p.part_id) === String(partId) ||
      String(p.crossCatKey) === String(partId)
    );
    if (!target) return;
    const label = String(target.crossCatKey || target.calloutLabel || target.callout || target.index || target.part_callout_id || '');
    if (!label) return;
    setSelectedCallout(label);
    setSelectedPartInfo({ ...partInfo, ...target });
  }, [autoOpenModal, partId, image_parts, partInfo]);

  const openModal = useCallback(() => setShowModal(true), []);
  const closeModal = useCallback(() => {
    setShowModal(false);
    // Restore the recommended part when closing so the details panel below the
    // image stays populated.
    if (partCalloutIds.length > 0) {
      const matchedCallout = callouts.find(c => partCalloutIds.includes(c.label));
      const matchedLabel = matchedCallout?.label ?? partCalloutIds[0];
      setSelectedCallout(matchedLabel);
      setSelectedPartInfo(findPartInfo(matchedLabel));
    } else {
      setSelectedCallout(null);
      setSelectedPartInfo(null);
    }
    onModalClose?.();
  }, [partCalloutIds, callouts, findPartInfo, onModalClose]);

  // Mounted purely to show the map (autoOpenModal): open it once on mount.
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpenModal && !autoOpenedRef.current) {
      autoOpenedRef.current = true;
      openModal();
    }
  }, [autoOpenModal, openModal]);

  // Translate the SELECTED part's description + category for the details panel
  // below the image. (The map's own list translates itself.)
  const translationKey = useMemo(() => {
    if (!selectedPartInfo) return null;
    const desc = selectedPartInfo.description || selectedPartInfo.name || '';
    const cat = selectedPartInfo.category || '';
    return `${desc}::${cat}::${i18n.language || ''}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPartInfo?.description, selectedPartInfo?.name, selectedPartInfo?.category, i18n.language]);
  useEffect(() => {
    setTranslatedDescription(null);
    setTranslatedCategory(null);
    setIsTranslating(false);
    if (!selectedPartInfo || !translationKey) return;

    const desc = selectedPartInfo.description || selectedPartInfo.name || '';
    const cat = selectedPartInfo.category || '';
    // Parent pre-translations first (avoids duplicate API calls).
    if (preTranslatedDescriptions) {
      const preDesc = desc ? preTranslatedDescriptions[desc] : '';
      const preCat = cat ? preTranslatedDescriptions[cat] : '';
      if (preDesc) setTranslatedDescription(preDesc);
      if (preCat) setTranslatedCategory(preCat);
      if ((preDesc || !desc) && (preCat || !cat)) return;
    }
    const cached = translationCacheRef.current.get(translationKey);
    if (cached) {
      if (cached.description) setTranslatedDescription(cached.description);
      if (cached.category) setTranslatedCategory(cached.category);
      return;
    }

    const items: Array<{ key: string; text: string }> = [];
    if (desc) items.push({ key: 'description', text: desc });
    if (cat) items.push({ key: 'category', text: cat });
    if (items.length === 0) return;

    let cancelled = false;
    setIsTranslating(true);
    TranslationBatchManager.request(String(getActiveOrderId() ?? ''), i18n.language, items)
      .then(result => {
        if (cancelled) return;
        const descTranslation = result.description || '';
        const catTranslation = result.category || '';
        translationCacheRef.current.set(translationKey, { description: descTranslation, category: catTranslation });
        if (descTranslation) setTranslatedDescription(descTranslation);
        if (catTranslation) setTranslatedCategory(catTranslation);
      })
      .catch(() => { /* leave the original on failure */ })
      .finally(() => { if (!cancelled) setIsTranslating(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translationKey, preTranslatedDescriptions]);

  // ── The request that titles the viewer ────────────────────────────────────
  // The corrected query is the hero field; the user's original term stays
  // visible beside it. resolvedLabel usually arrives as "Corrected (original)".
  // AI note for a part loaded WITHOUT a clarification question (a part
  // number, a single confident match). Guidance used to come only from an
  // answered question, so a directly loaded result showed no AI hint at all.
  // Asked once per part when its diagram opens, from the same (cached) hint
  // service — a single candidate gets "what to check to confirm it".
  const hintSessionId = useActiveSessionIdSafe();
  const [fetchedGuidance, setFetchedGuidance] = useState<Record<string, any>>({});
  const hintAsked = useRef<Set<string>>(new Set());
  const hintAlive = useRef(true);
  useEffect(() => {
    hintAlive.current = true;
    return () => { hintAlive.current = false; };
  }, []);
  useEffect(() => {
    const info: any = partInfo;
    if (!showModal || !info || info.choice_guidance?.candidate) return;
    const id = catalogPartIdentity(info);
    if (!id || hintAsked.current.has(id)) return;
    hintAsked.current.add(id);
    const label = String(info.description || info.name || info.partName || info.partId || '').trim();
    if (!label) return;
    const params: string[] = Array.isArray(info.part_parameters) ? info.part_parameters.map(String) : [];
    const requested = String(resolvedLabel || info.related_corrected_name || label).replace(/\s*\([^)]*\)\s*$/, '').trim() || label;
    const groupId = String(info.group_id || info.categoryId || '');
    const sectionId = String(info.section_id || '');
    const ref = {
      group_id: groupId, section_id: sectionId, partId: String(info.partId ?? ''),
      callout: info.crossCatKey != null ? String(info.crossCatKey) : (partCalloutId != null ? String(partCalloutId) : null),
      part_name: label, part_number: info.partNumber ?? null,
    };
    const ask = () => chatService.getClarificationHint({
      session_id: hintSessionId || null,
      question_id: null,
      part_name: requested,
      part_parameters: params,
      options: [{
        id: 'opt-0', label, callout: ref.callout, part_number: ref.part_number,
        extra_details: info.extra_details || null,
        probability_score: typeof info.probability_score === 'number' ? info.probability_score : null,
        section_key: groupId && sectionId ? `${groupId}/${sectionId}` : null,
        section_label: info.category || null,
      }],
      vehicle: vin_data || null,
      language: i18n.language || null,
    });
    const take = (hint: any) => {
      const cand = (hint?.candidates || []).find((c: any) => c?.option_id === 'opt-0');
      if (!hintAlive.current || !cand || !(cand.summary || cand.tags?.length)) return false;
      const note = {
        part_name: requested, part_parameters: params, part_refs: [ref], candidate: cand,
        probability_score: typeof info.probability_score === 'number' ? info.probability_score : null,
      };
      // Keyed by every spelling of the part's number: the card and the
      // diagram row name the same part as "A4571303457" / "A 457 130 34 57"
      // or by an internal id, and the note must reach the row either way.
      setFetchedGuidance(prev => ({ ...prev, ...Object.fromEntries(partKeys(info).map(k => [k, note])) }));
      return true;
    };
    ask().then(hint => {
      if (!take(hint) && !hint?.refining) console.info('[result-hint] no note returned for', id, hint);
      // A fast catalog-only answer first; the grounded one replaces it once.
      if (hint?.refining) setTimeout(() => { if (hintAlive.current) ask().then(take).catch(() => {}); }, 8000);
    }).catch((err: any) => {
      // Display-only: no note is better than an error on a result — but say why.
      console.info('[result-hint] request failed for', id, err?.response?.status, err?.response?.data?.detail || err?.message);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, partInfo]);

  const searchRequest = useMemo(() => {
    const original = String((partInfo as any)?.related_user_query ?? '').trim();
    const rlab = String(resolvedLabel ?? '').trim();
    const m = rlab.match(/^(.*?)\s*\(([^)]*)\)\s*$/);
    const corrected = (m ? m[1] : rlab).trim();
    const originalQ = original || (m ? m[2].trim() : '');
    const showCorrected = !!corrected && !!originalQ && corrected.toLowerCase() !== originalQ.toLowerCase();
    // What the customer's answers settled on the way to this part ("Complete
    // assembly"), minus words the request line already carries.
    const said = corrected.toLowerCase();
    const answered = ((partInfo as any)?.answered_as as unknown[] | undefined || [])
      .map(x => String(x || '').trim())
      .filter(x => x && !said.includes(x.toLowerCase()));
    return { corrected, originalQ, showCorrected, answered };
  }, [partInfo, resolvedLabel]);

  // The side (Left/Right) the user asked for — from the structured qualifier
  // they stated (part_parameters "side:Left"), else parsed from the request.
  const requestedSide = useMemo<'left' | 'right' | null>(() => {
    const params = (partInfo as any)?.part_parameters;
    if (Array.isArray(params)) {
      for (const p of params) {
        const m = String(p).match(/(?:side|lado)\s*[:=]\s*(.+)/i);
        if (m) { const s = positionClassesFromName(m[1]).side; if (s) return s; }
      }
    }
    for (const txt of [searchRequest.originalQ, searchRequest.corrected, String(resolvedLabel ?? '')]) {
      const s = positionClassesFromName(txt).side;
      if (s) return s;
    }
    return null;
  }, [partInfo, searchRequest, resolvedLabel]);

  // The recommended row is pinned to this exact request. Its EPC record can be
  // generic ("PANEL ASSEMBLY") and omit position columns, so retain the
  // resolved request's known Front/Rear + Left/Right context in the UI.
  // Catalog values stay authoritative and are never overwritten.
  const resolvedRequestPositions = useCallback((info: any): string[] => (
    resolvedPositionChips(info?.extra_details, [searchRequest.corrected, searchRequest.originalQ, resolvedLabel], (partInfo as any)?.part_parameters)
  ), [partInfo, searchRequest, resolvedLabel]);

  // A part's side, read from its dedicated side/position column first, then its
  // name. Null when undeterminable — such rows are never demoted (fail open).
  const sideOfRow = useCallback((info: any): 'left' | 'right' | null => {
    for (const c of positionChipsFromExtra(info?.extra_details)) {
      const s = positionClassesFromName(c).side;
      if (s) return s;
    }
    return positionClassesFromName(info?.description || info?.name || '').side;
  }, []);

  // Vehicle model year from VIN data — drives the OE-note date filter. Year
  // granularity only (no build date exists). SAIC leaves `year` empty and
  // carries it inside the model name ("ZS OVERSEAS LEFT 2017"), so model/trim
  // are scanned too.
  const vehicleYear = useMemo<number | null>(() => {
    for (const src of [vin_data?.year, vin_data?.model, vin_data?.trim]) {
      const m = String(src ?? '').match(/(?:19|20)\d{2}/);
      if (m) return parseInt(m[0], 10);
    }
    return null;
  }, [vin_data]);

  // ── Rows for the shared viewer ────────────────────────────────────────────
  // One row per (callout × catalog part), in CATALOG order so kit adjacency
  // survives. A candidate that doesn't fit the requested SIDE, or whose OE-note
  // production window excludes the vehicle's model year, is DEMOTED out of the
  // candidate block (never hidden). Both checks fail open.
  const rows: DiagramRow[] = useMemo(() => {
    const catalogOrder = new Map<string, number>();
    (Array.isArray(image_parts) ? image_parts : []).forEach((p, i) => {
      const id = catalogPartIdentity(p);
      if (id && !catalogOrder.has(id)) catalogOrder.set(id, i);
    });
    const seen = new Set<string>();
    const out: Array<{ row: DiagramRow; order: number }> = [];
    for (const c of callouts) {
      const label = String(c.label);
      const infos = findAllPartInfos(label);
      const list: Array<any | null> = infos.length ? infos : [null];
      list.forEach((info, i) => {
        // The same callout label can appear at SEVERAL spots on one diagram
        // (two air-cleaner boxes both tagged 28111) — one list row per part.
        const rowKey = `${label}#${info?.partNumber ?? info?.partId ?? info?.crossCatKey ?? i}`;
        if (seen.has(rowKey)) return;
        seen.add(rowKey);
        const priority = calloutPriority(label, info);
        const wrongSide = requestedSide != null && (() => { const sd = sideOfRow(info); return sd != null && sd !== requestedSide; })();
        const wrongDate = vehicleYear != null && noteDateVerdict(chipDataFor(info).notes.join(' | '), vehicleYear) === false;
        const demoted = (priority === 'hero' || priority === 'secondary') && (wrongSide || wrongDate);
        const candidate = (priority === 'hero' || priority === 'secondary') && !demoted;
        const selId = selectedPartInfo?.partNumber ?? selectedPartInfo?.partId ?? selectedPartInfo?.crossCatKey ?? null;
        const rowId = info?.partNumber ?? info?.partId ?? info?.crossCatKey ?? null;
        const selected = selectedCallout === label && (selId != null && rowId != null ? String(selId) === String(rowId) : true);
        // A confident match echoes the qualifiers the user asked for; an
        // estimate shows what the CATALOG says, which tells candidates apart.
        const extra = (priority === 'hero' && !isEstimatedScore(info?.probability_score) && info)
          ? { ...(info.extra_details || {}), ...Object.fromEntries(resolvedRequestPositions(info).map((p, n) => [`position_${n}`, p])) }
          : info?.extra_details;
        const id = info ? catalogPartIdentity(info) : '';
        out.push({
          row: {
            key: rowKey,
            callout: label,
            partId: info?.partId != null ? String(info.partId) : null,
            partNumber: info?.partNumber ?? null,
            name: info ? (info.description || info.name || info.partName || null) : null,
            extra_details: extra ?? null,
            axes: (info as any)?.axes ?? null,
            score: typeof info?.probability_score === 'number' ? info.probability_score : null,
            candidate,
            priority: demoted ? 'optional' : priority,
            selected,
            inCart: isPartInOrder(info, label),
            userPick: isUserPickedInfo(label, info),
            // The customer's own choice (an answered question, or a click on
            // the drawing) shows no match estimate — a model score next to
            // what a person decided reads as doubt ("Low" on their answer).
            confirmed: !!info && ((!!(partInfo as any)?.user_selected && isRequestedRow(info, label)) || isUserPickedInfo(label, info)),
            quantity: info?.quantity ?? null,
            bundleKey: id ? `id:${id}` : rowKey,
            raw: info,
          },
          order: id ? (catalogOrder.get(id) ?? Number.MAX_SAFE_INTEGER) : Number.MAX_SAFE_INTEGER,
        });
      });
    }
    return out.sort((a, b) => a.order - b.order).map(x => x.row);
  }, [callouts, image_parts, findAllPartInfos, calloutPriority, requestedSide, sideOfRow, vehicleYear, chipDataFor, selectedCallout, selectedPartInfo, isPartInOrder, isUserPickedInfo, resolvedRequestPositions, partInfo, isRequestedRow]);


  // Selecting a row marks its callout on the diagram and fills the details
  // panel below the image. Clicking the selected row again clears it.
  const onToggleRow = useCallback((row: DiagramRow, next: boolean) => {
    if (!next) {
      setSelectedCallout(null);
      setSelectedPartInfo(null);
      return;
    }
    setSelectedCallout(row.callout);
    setSelectedPartInfo(row.raw ?? findPartInfo(row.callout));
  }, [findPartInfo]);

  const userPickChip = (
    <span
      title={String(t('chat.multiPart.userSelectedTitle', { defaultValue: 'You chose this part when answering the question' }))}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 9, fontWeight: 700, color: '#137f67', background: '#c7ffe8', borderRadius: 6, padding: '2px 6px', whiteSpace: 'nowrap' }}
    >
      <Check size={9} strokeWidth={3} aria-hidden />
      {t('imageWithCallouts.tags.userPick', { defaultValue: 'Manual selection' })}
    </span>
  );

  const renderHeader = () => (
    // One compact strip — the same block as the question diagram. Status chip,
    // then our reading of the request — nothing else: no answer chips, no part
    // tags, no ambiguity note (the list and the chat carry those).
    <div className="diagram-results-header" style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '10px 14px 10px 12px', background: DIAGRAM_PALETTE.lilac, borderLeft: `4px solid ${DIAGRAM_PALETTE.navy}`, borderBottom: `0.5px solid ${DIAGRAM_PALETTE.border}`, minWidth: 0 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 10px', minWidth: 0 }}>
        <span style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 999, background: DIAGRAM_PALETTE.navy, color: '#fff', fontSize: 10.5, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          <Search size={11} strokeWidth={2.4} aria-hidden /> {t('imageWithCallouts.searchRequest', { defaultValue: 'Search results' })}
        </span>
        <span className="diagram-results-instruction" style={{ flex: '1 1 200px', minWidth: 0, fontSize: 12.5, fontWeight: 700, color: '#211f2b', lineHeight: 1.4, overflowWrap: 'anywhere' }}>
          {/* Our reading of the request, then what the answers settled — the
              customer's raw words are not repeated here (they read the request
              in the chat). */}
          <h2 style={{ margin: 0, display: 'inline', fontSize: 'inherit', fontWeight: 'inherit' }}>
            {searchRequest.corrected || selectedPartInfo?.description || t('imageWithCallouts.modal.title')}
          </h2>
        </span>
      </div>
    </div>
  );

  const renderFooter = ({ close }: { close: () => void }) => (
    <>
      <span style={{ flex: '1 1 220px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* No "Selected · your choice" line: the list marks the pick. */}
        {!(selectedCallout && selectedPartInfo) && (
          <span style={{ fontSize: 12, fontWeight: 500, color: DIAGRAM_PALETTE.textMuted }}>
            {t('imageWithCallouts.footer.selectHint', { defaultValue: 'Select a part on the diagram or in the list' })}
          </span>
        )}
      </span>
      <button
        type="button"
        onClick={close}
        style={{ borderRadius: 8, cursor: 'pointer', background: 'transparent', color: DIAGRAM_PALETTE.textPrimary, fontSize: 13, fontWeight: 600, fontFamily: 'Inter Tight, system-ui, sans-serif', border: `1px solid ${DIAGRAM_PALETTE.border}`, padding: '9px 16px' }}
      >{t('imageWithCallouts.modal.close')}</button>
      {selectedCallout && selectedPartInfo && (isPartInOrder(selectedPartInfo, selectedCallout) ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#137f67', background: '#c7ffe8', borderRadius: 8, padding: '8px 14px' }}>
          <Check size={14} strokeWidth={2.5} aria-hidden />
          {getActiveOrderId() ? t('imageWithCallouts.actions.alreadyInOrder') : t('imageWithCallouts.actions.alreadyInCart')}
        </span>
      ) : (
        <button
          type="button"
          onClick={addToCart}
          disabled={isAddingToOrder}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: 'none', borderRadius: 8, cursor: isAddingToOrder ? 'not-allowed' : 'pointer', background: DIAGRAM_PALETTE.navy, color: '#fff', fontSize: 13, fontWeight: 600, fontFamily: 'Inter Tight, system-ui, sans-serif', padding: '9px 18px', opacity: isAddingToOrder ? 0.6 : 1, boxShadow: '0 2px 10px rgba(104,102,255,0.25)' }}
        >
          <ShoppingCart size={14} strokeWidth={2} aria-hidden />
          {isAddingToOrder ? t('imageWithCallouts.actions.adding') : (getActiveOrderId() ? t('imageWithCallouts.actions.addToOrder') : t('imageWithCallouts.actions.addToCart'))} →
        </button>
      ))}
    </>
  );

  const inOrderOfSelected = selectedCallout ? isPartInOrder(selectedPartInfo, selectedCallout) : false;

  return (
    // `translate="no"` / `notranslate`: this subtree renders bilingual text
    // (catalog original + our in-app translation). Browser auto-translate
    // rewrites those text nodes out from under React and crashes it.
    <div translate="no" className="notranslate">
      {/* The card shows the WHOLE drawing, letterboxed (never cropped), with
          one action over it — same as the clarification card. Callouts, the
          parts list and selection live in the map. */}
      <div style={{ position: 'relative', width: '100%', background: '#fff', border: '1px solid #e3e2e8', borderRadius: 10, overflow: 'hidden' }}>
        <button
          type="button"
          aria-label={String(t('imageWithCallouts.openMap', { defaultValue: 'Open the map' }))}
          onClick={openModal}
          style={{ position: 'absolute', inset: 0, zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: 999, background: DIAGRAM_PALETTE.navy, color: '#fff', fontSize: 13, fontWeight: 600, padding: '9px 16px', boxShadow: '0 4px 14px rgba(0,0,0,0.28)' }}>
            <Maximize2 size={15} /> {t('imageWithCallouts.openMap', { defaultValue: 'Open the map' })}
          </span>
        </button>
        <img
          src={imageData}
          alt={String(t('imageWithCallouts.modal.title'))}
          draggable={false}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageLoaded(true)}
          style={{ display: 'block', width: '100%', height: 'auto', maxHeight: 300, objectFit: 'contain', background: '#fff' }}
        />
        {imageLoaded && callouts.length === 0 && (
          <div className="absolute top-2 left-2 max-w-[80%] pointer-events-none" style={{ zIndex: 3 }}>
            <div className="bg-amber-50 border border-amber-300 text-amber-900 text-[11px] leading-snug px-2 py-1 rounded shadow-sm">
              {t('imageWithCallouts.calloutNotAvailable', { defaultValue: 'Callout not available with current filtering' })}
            </div>
          </div>
        )}
      </div>

      {/* The shared fullscreen viewer — the same component the clarification
          question opens, so both maps read identically. */}
      <DiagramModal
        open={showModal}
        diagram={{ diagram_url: imageData, callouts }}
        // The catalog path of this drawing (group › section), the same
        // breadcrumb the question diagram shows.
        sectionName={partInfo?.category || null}
        groupName={partInfo?.category_group || null}
        rows={rows}
        onClose={closeModal}
        onToggleRow={onToggleRow}
        otherPartsOpen={false}
        render3d={(url, signal) => chatService.getDiagram3d(url, hintSessionId || chatService.getGuestSessionId(), signal)}
        build3dModel={model3d ? (url, parts, signal, rebuild, onProgress) => chatService.buildDiagram3dModel(url, parts, signal, rebuild, onProgress) : null}
        requestTexts={[searchRequest.corrected, searchRequest.originalQ, ...(((partInfo as any)?.part_parameters || []) as any[]).map(v => String(v).replace(/^[^:=]+[:=]\s*/, '').trim())]}
        preTranslations={preTranslatedDescriptions}
        legendExtras={[
          { kind: 'inCart', label: String(t('imageWithCallouts.tags.inOrder')) },
          ...(userPicked ? [{ kind: 'userPick' as const, label: String(t('imageWithCallouts.legend.userPick', { defaultValue: 'Your manual selection' })) }] : []),
        ]}
        renderHeader={renderHeader}
        renderFooter={renderFooter}
        // The AI's side / position / values join the card's own tag row (one
        // look, no duplicates: cardTags drops what the card already states);
        // the note under the card keeps only the "choose this when…" line.
        rowAiTags={(row, ctx) => {
          const guidance = row.raw?.choice_guidance
            || (row.raw ? partKeys(row.raw).map(k => fetchedGuidance[k]).find(Boolean) : null);
          if (!guidance?.candidate) return null;
          // The same reading → tags as the question viewer (aiRowTagsOf).
          return aiRowTagsOf(guidance, t as any, { exclude: ctx.shownValues, names: ctx.names });
        }}
        // The request's type drives the ✓ / ✗ functionality tag, exactly as a
        // question's part name does: the two viewers wear the same tags.
        requestedType={requestedTypeOf(searchRequest.corrected || resolvedLabel || (partInfo as any)?.related_corrected_name) || null}
        // No "choose when" note under a result row: the AI reading reaches the
        // row as tags (rowAiTags) and the list stays compact.
        rowBits={(row) => (row.userPick ? [userPickChip] : [])}
        renderRowAction={(row, { primary }) => {
          // The catalog's per-vehicle count goes INTO the button: "Add ×2" is
          // what one click puts in the cart (addInfoToCart uses the same count).
          const qty = parseCatalogQuantity(row.quantity);
          return row.inCart ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, color: '#fff', background: DIAGRAM_PALETTE.cart, borderRadius: 6, padding: '3px 8px', whiteSpace: 'nowrap' }}>
              <Check className="w-2.5 h-2.5" strokeWidth={3} />{t('imageWithCallouts.tags.inOrder')}
            </span>
          ) : row.raw ? (
            <button
              type="button"
              disabled={isAddingToOrder}
              onClick={() => addInfoToCart(row.callout, row.raw)}
              title={qty > 1 ? String(t('imageWithCallouts.actions.addQtyTitle', { defaultValue: 'This vehicle uses {{n}} — all {{n}} are added', n: qty })) : undefined}
              // Only the selected / best-match row keeps the filled navy Add;
              // the rest go ghost so four equal buttons don't flatten the list.
              data-variant={primary ? 'primary' : 'ghost'}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, color: primary ? '#fff' : DIAGRAM_PALETTE.navy, background: primary ? DIAGRAM_PALETTE.navy : 'transparent', border: `1px solid ${primary ? DIAGRAM_PALETTE.navy : 'rgba(30, 13, 184, 0.35)'}`, borderRadius: 8, padding: '4px 9px', cursor: isAddingToOrder ? 'not-allowed' : 'pointer', opacity: isAddingToOrder ? 0.6 : 1, whiteSpace: 'nowrap' }}
            >
              <ShoppingCart className="w-2.5 h-2.5" strokeWidth={2} />{t('imageWithCallouts.actions.add', { defaultValue: 'Add' })}
              {qty > 1 && <span data-testid="add-qty" style={{ fontWeight: 800 }}>×{qty}</span>}
            </button>
          ) : null;
        }}
      />

      {/* The selected part's details, under the image on the card. */}
      {selectedCallout && selectedPartInfo && (
        <div style={{ marginTop: '8px', backgroundColor: DIAGRAM_PALETTE.bgSurface, border: `0.5px solid ${DIAGRAM_PALETTE.border}`, borderRadius: '10px', overflow: 'hidden' }}>
          <div style={{ padding: '8px 12px 6px 12px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            {isUserPickedInfo(selectedCallout, selectedPartInfo) && userPickChip}
            {inOrderOfSelected && (
              <span style={{ padding: '2px 8px', fontSize: '11px', fontWeight: 600, lineHeight: '16px', backgroundColor: '#c7ffe8', color: '#137f67', borderRadius: '40px', whiteSpace: 'nowrap' }}>
                {t('imageWithCallouts.tags.inOrder')}
              </span>
            )}
            {(() => {
              const score = displayProbability ?? selectedPartInfo?.probability_score;
              const band = typeof score === 'number' && score > 0 ? matchBand(score) : null;
              if (!band) return null;
              return (
                <span style={{ padding: '2px 8px', fontSize: '11px', fontWeight: 600, lineHeight: '16px', backgroundColor: inOrderOfSelected ? '#d3edff' : '#c3eff4', color: inOrderOfSelected ? '#1176a2' : '#1193a2', borderRadius: '40px', whiteSpace: 'nowrap' }}>
                  {t(MATCH_BAND_LABEL[band].key, { defaultValue: MATCH_BAND_LABEL[band].defaultValue })}
                </span>
              );
            })()}
          </div>

          <div style={{ padding: '2px 12px 8px 12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {selectedPartInfo['partNumber'] && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                <span style={{ fontSize: '10px', fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: '0.04em' }}>OE {t('imageWithCallouts.partInfo.number') || 'Number'}</span>
                <span style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '13px', fontWeight: 700, lineHeight: '18px', color: '#201e1a', wordBreak: 'break-all', flex: 1 }}>{displayPartRef(String(selectedPartInfo['partNumber']))}<SupersessionIndicator row={selectedPartInfo as any} /></span>
                  <span style={{ flexShrink: 0, fontSize: '10px', fontWeight: 500, color: '#888', backgroundColor: '#f1f0f3', borderRadius: '4px', padding: '1px 5px', lineHeight: '15px', whiteSpace: 'nowrap' }}>
                    {t('imageWithCallouts.original') || 'Original'}
                  </span>
                </span>
              </div>
            )}
            {selectedPartInfo['description'] && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                <span style={{ fontSize: '10px', fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  OE {t('imageWithCallouts.partInfo.description')}
                </span>
                <span style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '13px', fontWeight: 700, lineHeight: '18px', color: '#201e1a', wordBreak: 'break-word', flex: 1 }}>
                    {String(selectedPartInfo['description'])}
                  </span>
                  <span style={{ flexShrink: 0, fontSize: '10px', fontWeight: 500, color: '#888', backgroundColor: '#f1f0f3', borderRadius: '4px', padding: '1px 5px', lineHeight: '15px', whiteSpace: 'nowrap' }}>
                    {t('imageWithCallouts.original') || 'Original'}
                  </span>
                </span>
                {isTranslating ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#888' }}>
                    <span className="inline-block w-2.5 h-2.5 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin flex-shrink-0" />
                    <span>{t('imageWithCallouts.translating') || 'Translating…'}</span>
                  </span>
                ) : translatedDescription && translatedDescription !== String(selectedPartInfo['description']) ? (
                  <span style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '12px', fontWeight: 500, lineHeight: '17px', color: '#5a5a5a', wordBreak: 'break-word', flex: 1 }}>
                      {translatedDescription}
                    </span>
                    <span style={{ flexShrink: 0, fontSize: '10px', fontWeight: 500, color: '#1E0DB8', backgroundColor: '#E5E2FF', borderRadius: '4px', padding: '1px 5px', lineHeight: '15px', whiteSpace: 'nowrap' }}>
                      {t('imageWithCallouts.translated') || 'Translated'}
                    </span>
                  </span>
                ) : null}
              </div>
            )}
            {selectedPartInfo['category'] && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                <span style={{ fontSize: '10px', fontWeight: 500, color: '#999', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  OE {t('imageWithCallouts.partInfo.category')}
                </span>
                <span style={{ fontSize: '12px', fontWeight: 600, lineHeight: '18px', color: '#201e1a', wordBreak: 'break-word' }}>
                  {String(selectedPartInfo['category'])}
                </span>
                {translatedCategory && translatedCategory !== String(selectedPartInfo['category']) && (
                  <span style={{ fontSize: '12px', fontWeight: 500, lineHeight: '17px', color: '#5a5a5a', wordBreak: 'break-word' }}>{translatedCategory}</span>
                )}
              </div>
            )}
            {/* Side/position chips — the same qualifier the question diagram surfaces. */}
            {(() => {
              const positions = partCalloutIds.includes(String(selectedCallout))
                ? resolvedRequestPositions(selectedPartInfo)
                : positionChipsFromExtra((selectedPartInfo as any)['extra_details']);
              if (!positions.length) return null;
              return (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span style={{ width: '72px', flexShrink: 0, fontSize: '12px', fontWeight: 400, lineHeight: '18px', color: '#999' }}>{t('chat.multiPart.position', { defaultValue: 'Position' })}</span>
                  <span style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', flex: 1 }}>
                    {positions.map((p, i) => (
                      <span key={i} style={{ flexShrink: 0, fontSize: '10px', fontWeight: 600, color: '#3627BF', backgroundColor: '#eef0ff', border: '1px solid #d6d8ff', borderRadius: '6px', padding: '1px 6px', whiteSpace: 'nowrap' }}>
                        {tPos(p) !== p ? tPos(p) : (preTranslatedDescriptions?.[p] || p)}
                      </span>
                    ))}
                  </span>
                </div>
              );
            })()}
            {/* OE Notes — remark / notes / optional-package values. */}
            {(() => {
              const notes = chipDataFor(selectedPartInfo).notes;
              if (!notes.length) return null;
              return (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <span style={{ width: '72px', flexShrink: 0, fontSize: '12px', fontWeight: 400, lineHeight: '18px', color: '#999' }}>{t('chat.multiPart.oeNotes', { defaultValue: 'OE Notes' })}</span>
                  <span style={{ fontSize: '12px', fontWeight: 600, lineHeight: '18px', color: '#201e1a', flex: 1, overflowWrap: 'break-word' }}>
                    {notes.map((val, i) => {
                      const tn = preTranslatedDescriptions?.[val];
                      return <span key={i}>{i > 0 ? '; ' : ''}{tn || val}{!!tn && tn.trim() !== val.trim() && <span style={{ marginLeft: 4, fontSize: '9px', fontWeight: 600, color: '#1E0DB8', backgroundColor: '#E5E2FF', borderRadius: '4px', padding: '0 5px', whiteSpace: 'nowrap' }}>{t('chat.multiPart.translated', { defaultValue: 'Translated' })}</span>}</span>;
                    })}
                  </span>
                </div>
              );
            })()}
            {selectedPartInfo['from'] && selectedPartInfo['from'] !== '--' && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <span style={{ width: '72px', flexShrink: 0, fontSize: '12px', fontWeight: 400, lineHeight: '18px', color: '#999' }}>{t('imageWithCallouts.partInfo.from')}</span>
                <span style={{ fontSize: '12px', fontWeight: 600, lineHeight: '18px', color: '#201e1a', flex: 1 }}>{String(selectedPartInfo['from'])}</span>
              </div>
            )}
            {selectedPartInfo['quantity'] && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <span style={{ width: '72px', flexShrink: 0, fontSize: '12px', fontWeight: 400, lineHeight: '18px', color: '#999' }}>{t('imageWithCallouts.partInfo.quantity') || 'Qty'}</span>
                <span style={{ fontSize: '12px', fontWeight: 600, lineHeight: '18px', color: '#201e1a', flex: 1 }}>{String(selectedPartInfo['quantity'])}</span>
              </div>
            )}
          </div>

          <div style={{ padding: inOrderOfSelected ? '0 12px 10px 12px' : '0 12px 12px 12px' }}>
            {inOrderOfSelected ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '5px' }}>
                <Check size={14} strokeWidth={3} color="#137f67" aria-hidden />
                <span style={{ fontSize: '12px', fontWeight: 600, lineHeight: '18px', color: '#137f67' }}>
                  {getActiveOrderId() ? t('imageWithCallouts.actions.alreadyInOrder') : t('imageWithCallouts.actions.alreadyInCart')}
                </span>
              </div>
            ) : (
              <button
                onClick={addToCart}
                disabled={isAddingToOrder}
                style={{ width: '100%', height: '34px', border: '1px solid #1E0DB8', borderRadius: '8px', backgroundColor: 'transparent', color: '#1E0DB8', fontSize: '12px', fontWeight: 600, lineHeight: '18px', cursor: isAddingToOrder ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', opacity: isAddingToOrder ? 0.6 : 1 }}
              >
                <ShoppingCart size={13} strokeWidth={2} aria-hidden />
                {isAddingToOrder ? t('imageWithCallouts.actions.adding') : (getActiveOrderId() ? t('imageWithCallouts.actions.addToOrder') : t('imageWithCallouts.actions.addToCart'))}
              </button>
            )}
          </div>
        </div>
      )}

      {/* A selected callout with no catalog details: either a "you may also
          like" suggestion (offer to add it) or genuinely nothing to show. */}
      {selectedCallout && !selectedPartInfo && (() => {
        const isYml = isYouMayLikePart(selectedCallout);
        const ymlMatch = isYml && youMayLikeParts
          ? youMayLikeParts.find(yp => String(yp.suggested_part_id) === String(selectedCallout) || (yp.suggested_part_name || '').toLowerCase() === selectedCallout.toLowerCase())
            ?? youMayLikeParts.find(yp => {
              const ymlName = (yp.suggested_part_name || '').toLowerCase();
              return ymlName.includes(selectedCallout.toLowerCase()) || selectedCallout.toLowerCase().includes(ymlName);
            })
          : null;
        return (
          <div style={{ marginTop: '8px', backgroundColor: isYml ? '#f6f4f0' : '#fffbeb', border: isYml ? undefined : '1px solid #fde68a', borderRadius: '10px', padding: '10px 12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: isYml ? '8px' : '0' }}>
              <div style={{ width: '24px', height: '24px', backgroundColor: isYml ? '#e5e2ff' : '#fef3c7', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 600, color: isYml ? '#1E0DB8' : '#b45309', flexShrink: 0 }}>
                {selectedCallout}
              </div>
              <span style={{ fontSize: '13px', fontWeight: 500, color: '#201e1a', flex: 1 }}>
                {ymlMatch ? ymlMatch.suggested_part_name : `${t('imageWithCallouts.partsTable.part', { defaultValue: 'Part' })} ${selectedCallout}`}
              </span>
              <button
                type="button"
                onClick={() => setSelectedCallout(null)}
                aria-label={String(t('chat.clarification.close', { defaultValue: 'Close' }))}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#999', display: 'flex', padding: 2 }}
              ><X size={14} /></button>
            </div>
            {!isYml ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', color: '#b45309' }}>
                <AlertTriangle style={{ width: '14px', height: '14px', flexShrink: 0 }} strokeWidth={2.2} />
                <span style={{ fontSize: '12px' }}>
                  {t('imageWithCallouts.partInfo.noInfo', { defaultValue: 'No additional information available' })}
                </span>
              </div>
            ) : (
              <button
                onClick={() => addYmlPartToCart(selectedCallout)}
                disabled={isAddingToOrder}
                style={{ width: '100%', height: '34px', border: '1px solid #1E0DB8', borderRadius: '8px', backgroundColor: 'transparent', color: '#1E0DB8', fontSize: '12px', fontWeight: 600, lineHeight: '18px', cursor: isAddingToOrder ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', opacity: isAddingToOrder ? 0.6 : 1 }}
              >
                <ShoppingCart size={13} strokeWidth={2} aria-hidden />
                {isAddingToOrder ? t('imageWithCallouts.actions.adding') : (getActiveOrderId() ? t('imageWithCallouts.actions.addToOrder') : t('imageWithCallouts.actions.addToCart'))}
              </button>
            )}
          </div>
        );
      })()}

      <Toast {...toast} onClose={hideToast} position="bottom-right" />
    </div>
  );
};

export default ImageWithCallouts;
